import { useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";

// Create/edit for one user. Username is fixed once created (it's the login identifier and the FK
// target for role assignment elsewhere), and the password field is optional in edit mode — leaving it
// blank keeps the existing password rather than clearing it.
export default function UserFormModal({ mode, initial, roles, saving, error, onSubmit, onClose }) {
  const isEdit = mode === "edit";
  const [username, setUsername] = useState(initial?.username || "");
  const [password, setPassword] = useState("");
  // Several roles may be ticked; the user gets the combined access of all of them.
  const [picked, setPicked] = useState(() => new Set(initial?.roles || (initial?.role ? [initial.role] : [])));

  function toggleRole(name) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }
  const [isAdmin, setIsAdmin] = useState(initial?.isAdmin || false);

  function submit(e) {
    e.preventDefault();
    // Keep the order of the role list, so the user's "first" role is predictable.
    const roleNames = roles.map((r) => r.name).filter((n) => picked.has(n));
    onSubmit({ username: username.trim(), password, roles: roleNames, isAdmin });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">{isEdit ? `Edit user "${initial.username}"` : "Create user"}</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <Form onSubmit={submit}>
          <div className="form-modal-body">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={isEdit}
                autoFocus={!isEdit}
              />
              {isEdit && <Form.Text className="text-muted">Username can't be changed after creation.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{isEdit ? "New password" : "Password"}</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!isEdit}
                placeholder={isEdit ? "Leave blank to keep the current password" : ""}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Roles</Form.Label>
              <div className="form-hint">
                Tick one or more. The user gets everything any of these roles grants — tables, columns and
                documents combined.
              </div>
              {roles.length === 0 ? (
                <div className="col-desc-hint">No roles yet — create them on the Roles page.</div>
              ) : (
                <div className="user-role-picks">
                  {roles.map((r) => (
                    <Form.Check
                      key={r.id}
                      type="checkbox"
                      id={`user-role-${r.id}`}
                      label={r.name}
                      checked={picked.has(r.name)}
                      disabled={isAdmin}
                      onChange={() => toggleRole(r.name)}
                    />
                  ))}
                </div>
              )}
              {isAdmin && (
                <Form.Text className="text-muted">An admin sees every table and document, so roles have no effect.</Form.Text>
              )}
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="user-modal-admin"
              label="Admin (sees all tables, manages users)"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
          </div>
          <div className="form-modal-foot">
            <Button variant="outline-secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : isEdit ? "Save changes" : "Create user"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
